import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, Typography } from '@heroui/react';
import { ErrorFallback } from '@/components/errorBoundary';
import { PageNotice } from '@/components/pageNotice';
import { PATH } from '@/router/PATH';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ImportStepBody, ImportStepStrip } from './components';
import type { BranchImportPageState } from './useBranchImportPage';

type BranchImportPageViewProps = {
    page: BranchImportPageState;
};

/**
 * The in-shell import page under the shell's navbar, which carries the title
 * ("Import products", the branch beside it) and Back to Branches:
 *
 * - **ready:** the step strip in `shell` mode (File · Names · Products ·
 *   Result, the steps behind as links back), a line saying the file goes into this branch and nowhere else,
 *   and the current step (on File, the branch's import history under it).
 * - **loading:** the strip, without links, and a placeholder where the
 *   branch line goes.
 * - **error:** the branch list did not load, with Try again.
 * - **notFound / forbidden:** the notice the other pages use, with a way
 *   back to the branch list.
 */
export const BranchImportPageView: React.FC<BranchImportPageViewProps> = ({
    page,
}) => {
    const { t } = useTranslation('pages');
    const backToBranches = {
        label: t('catalogImport.branchImport.backToBranches'),
        to: PATH.BRANCHES.ROOT,
    };

    switch (page.status) {
        case 'notFound':
            return (
                <PageNotice
                    action={backToBranches}
                    body={t('catalogImport.branchImport.notFound.body')}
                    icon="gravity-ui:circle-exclamation"
                    testId="branch-import-not-found"
                    title={t('catalogImport.branchImport.notFound.title')}
                    tone="warning"
                />
            );
        case 'forbidden':
            return (
                <PageNotice
                    action={backToBranches}
                    body={t('catalogImport.branchImport.forbidden.body')}
                    icon="gravity-ui:lock"
                    testId="branch-import-forbidden"
                    title={t('catalogImport.branchImport.forbidden.title')}
                    tone="warning"
                />
            );
        case 'error':
            return (
                <ErrorFallback
                    data-testid="branch-import-load-error"
                    id="branch-import-load-error"
                    message={t('catalogImport.branchImport.loadError.message')}
                    onRetry={page.onRetry}
                    retryLabel={t('catalogImport.branchImport.loadError.retry')}
                    title={t('catalogImport.branchImport.loadError.title')}
                />
            );
        case 'loading':
        case 'ready':
            return (
                <section
                    aria-busy={page.status === 'loading'}
                    className="flex w-full flex-col gap-4"
                    {...getNodeTestIdProp('branch-import-page')}
                >
                    <ImportStepStrip
                        branchId={
                            page.status === 'ready' ? page.branch.id : null
                        }
                        currentStep={page.step}
                    />
                    {page.status === 'ready' ? (
                        <>
                            <Typography
                                color="muted"
                                type="body-sm"
                                {...getNodeTestIdProp(
                                    'branch-import-fixed-branch',
                                )}
                            >
                                {t('catalogImport.branchImport.fixedBranch', {
                                    branch: page.branch.name,
                                })}
                            </Typography>
                            <ImportStepBody
                                branchId={page.branch.id}
                                paths={page.paths}
                                step={page.step}
                            />
                        </>
                    ) : (
                        <Skeleton
                            className="h-5 w-64 rounded-md"
                            {...getNodeTestIdProp(
                                'branch-import-branch-loading',
                            )}
                        />
                    )}
                </section>
            );
    }
};
